<template>
  <!-- Slider main container -->
  <div :class="['swiper-container', parentClass]">
    <!-- Additional required wrapper -->
    <div class="swiper-wrapper">
      <!-- Slides -->
      <slot></slot>
    </div>

    <!-- If we need pagination -->
    <div class="swiper-pagination"></div>

    <!-- If we need navigation buttons -->
    <div v-show="!hasReachedStart" class="swiper-button-prev">
      <div class="swiper-icon-wrapper">
        <div class="swiper-icon-bg">
          <ChevronLeftIcon v-if="swiperOptions.chevrons" class="swiper-icon" />
          <LeftArrows v-else class="swiper-icon" />
        </div>
      </div>
    </div>
    <div v-show="!hasReachEnd" class="swiper-button-next">
      <div class="swiper-icon-wrapper">
        <div class="swiper-icon-bg">
          <ChevronRightIcon v-if="swiperOptions.chevrons" class="swiper-icon" />
          <RightArrows v-else class="swiper-icon" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, onMounted, ref, watch } from '@vue/composition-api'
import Swiper from 'swiper/bundle'
import LeftArrows from '@/assets/icons/navigation/left_double_arrows.svg'
import RightArrows from '@/assets/icons/navigation/right_double_arrows.svg'
import { ChevronLeftIcon, ChevronRightIcon } from 'vue-feather-icons'

export default defineComponent({
  name: 'Swiper',
  components: {
    LeftArrows: { ...LeftArrows, name: 'LeftArrows' },
    RightArrows: { ...RightArrows, name: 'RightArrows' },
    ChevronLeftIcon,
    ChevronRightIcon,
  },
  props: {
    swiperOptions: {
      type: Object,
      required: true,
    },
    parentClass: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    swiperReset: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  setup(props, { emit }) {
    const hasReachedStart = ref(true)
    const hasReachEnd = ref(false)
    const options = {
      ...props.swiperOptions,
      init: false,
      observer: true,
      resizeObserver: true,
      grabCursor: true,
      freeMode: true,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
        disabledClass: 'swiper-button-disabled',
      },
    }
    const swiper = new Swiper(`.swiper-container.${props.parentClass}`, options)
    swiper.on('slideChange', evt => {
      emit('slide-changed', evt)
      hasReachedStart.value = evt.isBeginning
      hasReachEnd.value = evt.isEnd
    })
    swiper.on('reachBeginning', evt => emit('first-slide', evt))
    watch(
      () => props.swiperReset,
      currentStatus => {
        if (currentStatus) swiper.slidePrev()
      }
    )
    onMounted(() => {
      swiper.init()
      if (props.disabled) {
        swiper.disable()
      }
    })
    return {
      hasReachedStart,
      hasReachEnd,
    }
  },
})
</script>

<style lang="scss">
.swiper-container {
  @import '~swiper/swiper-bundle.css';
  .swiper-button-prev,
  .swiper-button-next {
    color: #fff;
    font-weight: 700;
    transition: all 0.5s ease;
    height: 100%;
    top: 0;
    bottom: 0;
    margin: 0;
    background: none;
    width: auto;
    &:hover {
      opacity: 1;
    }
    &:after {
      display: none;
    }
  }
  .swiper-button-prev {
    left: 0;
    @include mobile {
      width: 70px;
    }
  }
  .swiper-button-next {
    right: 0;
    @include mobile {
      width: 150px;
    }
  }
  .swiper-button-disabled {
    display: none;
  }
  .swiper-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    transition: 0.2s all ease;
    &:hover {
      transform: scale(1.2);
    }
  }
  .swiper-icon-bg {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    border-radius: 100%;
    background-color: rgba(0, 0, 0, 0.7);
  }
  .swiper-icon {
    fill: var(--white);
    min-width: 18px;
    height: 18px;
  }
}
</style>
